import { useState, useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import { submitMFA, resetMFA } from './actions';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';
import mfaVerificationReducer from './reducer';
import mfaVerificationSaga from './saga';
import LogoImage from '../logoTop.svg';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import CTAButton from '@components/CTAButton';
import CustomTextInput from '@components/CustomTextInput';
import {
  ScreenContainer,
  SignInComponentContainer,
  SignInInputAreaContainer,
  SignInHeaderContainer,
  InputContainer,
  SignInButtonContainer,
  ServerErrorMessage,
} from '../SignIn/styles';
import useMfaChallengeContext from './useMfaChallengeContext';
import BackToSignInLink from '../shared/BackToSignInLink';
import { getCodeSentMessage } from '../shared/codeDeliveryDetails';

const MFAVerificationPage = ({ t }) => {
  const [code, setCode] = useState('');
  const [missingParamsError, setMissingParamsError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const location = useLocation();
  const dispatch = useDispatch();

  // Redux state
  const loading = useSelector((state) => state.getIn(['mfaVerification', 'loading']));
  const error = useSelector((state) => state.getIn(['mfaVerification', 'error']));

  const { username, challengeName, session, deliveryMedium, destination, shouldSyncPasswordChangedAt } = useMfaChallengeContext(
    location.state,
  );

  const resolvedDeliveryMedium = deliveryMedium || (
    challengeName === 'SMS_MFA'
      ? 'SMS'
      : challengeName === 'EMAIL_OTP' || challengeName === 'CUSTOM_CHALLENGE'
        ? 'EMAIL'
        : null
  );
  const codeSentMessage = useMemo(() => {
    if (!destination) return null;
    return getCodeSentMessage({
      t,
      codeDeliveryDetails: { deliveryMedium: resolvedDeliveryMedium, destination },
    });
  }, [resolvedDeliveryMedium, destination, t]);

  const resolvedErrorMessage = useMemo(() => {
    if (!error) return null;

    const errorI18nKey = error && typeof error.get === 'function' ? error.get('i18nKey') : null;
    return t(errorI18nKey || 'Errors.mfa.signInFailed');
  }, [error, t]);

  useEffect(() => {
    dispatch(resetMFA());
  }, []);

  useEffect(() => {
    if (!username || !challengeName || !session) {
      setMissingParamsError(t('Errors.mfa.missingSession'));
    } else {
      setMissingParamsError(null);
    }
  }, [username, challengeName, session, t]);

  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (!code) {
      setFieldError(t('Errors.mfa.enterCode'));
      return;
    }
    if (!username || !challengeName || !session) {
      setFieldError(t('Errors.mfa.missingSession'));
      return;
    }
    setFieldError(null);
    dispatch(submitMFA({ username, code, session, challengeName, shouldSyncPasswordChangedAt }));
  };

  return (
    <ScreenContainer>
      <SignInComponentContainer>
        <SignInHeaderContainer>
          <img src={LogoImage} alt="logo" style={{ marginBottom: 60, width: 300 }} />
          <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H1}>
            {t('Authentication.MFA.title')}
          </Text>
        </SignInHeaderContainer>
        <SignInInputAreaContainer>
          {missingParamsError ? (
            <>
              <ServerErrorMessage type="CUSTOM" customText={missingParamsError} />
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <InputContainer>
                <CustomTextInput
                  type="text"
                  id="mfa_code"
                  label={t('Authentication.Verification.enterCode')}
                  name="mfa_code"
                  value={code}
                  error={fieldError}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  required
                />
                {codeSentMessage && (
                  <div style={{ marginTop: 8, color: '#555', fontSize: 14 }}>
                    {codeSentMessage}
                  </div>
                )}
              </InputContainer>
              <SignInButtonContainer>
                <CTAButton
                  id="mfa_verify_button"
                  onClick={handleSubmit}
                  expanded
                  disabled={loading || !code}
                >
                  {loading ? t('Actions.Auth.verifying') : t('Actions.Auth.verify')}
                </CTAButton>
                {error && (
                  <ServerErrorMessage
                    type="CUSTOM"
                    customText={resolvedErrorMessage}
                  />
                )}
              </SignInButtonContainer>
            </form>
          )}

          <BackToSignInLink t={t} />
        </SignInInputAreaContainer>
      </SignInComponentContainer>
    </ScreenContainer>
  );
};

const withReducer = injectReducer({ key: 'mfaVerification', reducer: mfaVerificationReducer });
const withSaga = injectSaga({ key: 'mfaVerification', saga: mfaVerificationSaga });

export default compose(
  withReducer,
  withSaga,
  withTranslation(),
)(MFAVerificationPage);
